import { useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Edit3, Trash2, Share2, UsersRound, Star } from 'lucide-react';
import { cn, truncateText } from '@/lib/utils';
import { Conversation, Project } from '@/types';
import DeleteConversationDialog from '@/components/DeleteConversationDialog';
import RenameDialog from './RenameDialog';
import { showToast } from '@/components/Toast';

// XSS validation: check for dangerous HTML/script characters
const containsXSS = (text: string): boolean => {
  const xssPattern = /<[^>]*>|javascript:|on\w+\s*=|&#|&lt;|&gt;/i;
  return xssPattern.test(text);
};
interface ConversationListProps {
  conversations: Conversation[];
  currentConversationId?: string;
  shareConversationId?: string;
  isLoading: boolean;
  hasLoadingConversations: boolean;
  projects?: Project[]; // 🔥 项目列表，用于判断 viewer 角色
  onConversationSelect: (id: string) => void;
  onRenameConversation: (id: string, title: string) => void;
  onDeleteConversation: (id: string) => Promise<void>;
  onShareConversation: (conversation: Conversation) => void;
  onSelectedNavItem: (item: string) => void;
  onBackToChat: () => void;
  /** 🔥 Star/Unstar conversation */
  onToggleStar?: (conversationId: string, currentStarred: boolean) => void;
}

// 🔥 优化：使用 memo 避免不必要的重新渲染
// 自定义比较函数：只在 conversations 的内容真正变化时才重新渲染
function areConversationsEqual(
  prevProps: ConversationListProps,
  nextProps: ConversationListProps
): boolean {
  // 如果conversations数组长度不同，肯定变了
  if (prevProps.conversations.length !== nextProps.conversations.length) {
    return false;
  }

  // 检查其他简单props
  if (
    prevProps.currentConversationId !== nextProps.currentConversationId ||
    prevProps.isLoading !== nextProps.isLoading ||
    prevProps.hasLoadingConversations !== nextProps.hasLoadingConversations
  ) {
    return false;
  }

  // 深度比较conversations数组：检查每个conversation的id、title和starred状态
  // 注意：不检查messages等嵌套数据，侧边栏不需要它们
  for (let i = 0; i < prevProps.conversations.length; i++) {
    const prev = prevProps.conversations[i];
    const next = nextProps.conversations[i];
    if (
      prev.id !== next.id ||
      prev.title !== next.title ||
      prev.isLoading !== next.isLoading ||
      prev.starred !== next.starred
    ) {
      return false;
    }
  }

  return true;
}

const ConversationList = memo(function ConversationList({
  conversations,
  currentConversationId,
  shareConversationId,
  isLoading,
  // hasLoadingConversations, // 🔥 不再使用，改为检查 conversation.isLoading
  projects = [],
  onConversationSelect,
  onRenameConversation,
  onDeleteConversation,
  onShareConversation,
  onToggleStar,
}: ConversationListProps) {
  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);
  // 重命名对话框状态
  const [renameDialog, setRenameDialog] = useState<{
    isOpen: boolean;
    conversationId: string | null;
    conversationTitle: string;
  }>({ isOpen: false, conversationId: null, conversationTitle: '' });
  const [conversationConfirmDialog, setConversationConfirmDialog] = useState<{
    isOpen: boolean;
    conversationId?: string;
    conversationTitle?: string;
  }>({ isOpen: false });

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // 打开重命名对话框
  const handleRename = (conversation: Conversation) => {
    if (conversation.isLoading) return;
    setRenameDialog({
      isOpen: true,
      conversationId: conversation.id,
      conversationTitle: conversation.title,
    });
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
        <span className="ml-2 text-sm text-gray-500">
          {t('sidebar.loadingConversations')}
        </span>
      </div>
    );
  }
  if (conversations.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">{t('sidebar.noConversations')}</p>
      </div>
    );
  }
  return (
    <>
      {conversations.map(conversation => {
        // 🔥 计算 viewer 角色（用于禁用 Rename 和 Delete）
        const conversationProject = conversation.projectId
          ? projects.find(p => p.id === conversation.projectId)
          : null;
        const projectRole = conversationProject?.role || null;
        const isConversationViewer = projectRole === 'viewer';

        return (
          <div
            key={conversation.id}
            className={cn(
              'group relative flex items-start gap-3 pl-2 py-1 mb-1 rounded-lg cursor-pointer transition-colors',
              currentConversationId === conversation.id
                ? 'bg-[#e8e6dc] border border-gray-200 shadow-sm'
                : 'hover:bg-[#e8e6dc]'
            )}
            onClick={() => {
              // 如果点击的是当前已选中的 conversation，则不做任何响应
              if (currentConversationId === conversation.id) {
                return;
              }
              // 直接调用 onConversationSelect，hash 路由会处理所有状态变更
              onConversationSelect(conversation.id);
            }}
          >
            <div className="flex-1 min-w-0">
              <div>
                <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                  {conversation.conversationType === 'group' && (
                    <UsersRound
                      size={14}
                      className="text-gray-600 flex-shrink-0"
                    />
                  )}
                  <div className="text-sm font-medium text-gray-800 truncate leading-relaxed">
                    {truncateText(conversation.title, 35)}
                  </div>
                  {conversation.isLoading && (
                    <div
                      className="animate-spin rounded-full h-3 w-3 border-b border-gray-400 flex-shrink-0"
                      title={t('sidebar.loading')}
                    ></div>
                  )}
                </div>
              </div>
            </div>
            {/* Edit and Delete buttons - disabled if sharing or if conversation is loading */}
            {/* 🔥 修复：只有当前 loading 的 conversation 禁止显示操作按钮，其他 conversation 不受影响 */}
            {!shareConversationId && !conversation.isLoading && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden items-center gap-1 group-hover:flex transition-all bg-[#e8e6dc] rounded-md px-1">
                {/* 🔥 Star/Unstar 按钮 */}
                {onToggleStar && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onToggleStar(
                        conversation.conversationId || conversation.id,
                        !!conversation.starred
                      );
                    }}
                    className={cn(
                      'p-1.5 rounded transition-colors',
                      conversation.starred
                        ? 'text-yellow-500 hover:bg-yellow-50 hover:text-yellow-600'
                        : 'text-gray-500 hover:bg-[#e8e6dc] hover:text-yellow-500'
                    )}
                    title={
                      conversation.starred ? t('chat.unstar') : t('chat.star')
                    }
                  >
                    <Star
                      size={12}
                      className="text-yellow-500"
                      fill={conversation.starred ? 'currentColor' : 'none'}
                    />
                  </button>
                )}
                {/* 🔥 viewer 角色禁用 Rename */}
                <button
                  onClick={e => {
                    if (isConversationViewer) return;
                    e.stopPropagation();
                    handleRename(conversation);
                  }}
                  disabled={isConversationViewer}
                  className={`p-1.5 rounded transition-colors ${
                    isConversationViewer
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-[#e8e6dc] hover:text-gray-700'
                  }`}
                  title={t('common.rename')}
                >
                  <Edit3 size={12} />
                </button>
                {/* 🔥 viewer 角色禁用 Delete */}
                <button
                  onClick={e => {
                    if (isConversationViewer) return;
                    e.stopPropagation();
                    setConversationConfirmDialog({
                      isOpen: true,
                      // 🔥 修复：使用后端 conversationId 而不是前端 id（temp-xxx）
                      conversationId:
                        conversation.conversationId || conversation.id,
                      conversationTitle: conversation.title,
                    });
                  }}
                  disabled={isConversationViewer}
                  className={`p-1.5 rounded transition-colors ${
                    isConversationViewer
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-red-100 hover:text-red-600'
                  }`}
                  title={t('common.delete')}
                >
                  <Trash2 size={12} />
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onShareConversation(conversation);
                  }}
                  className="p-1.5 rounded text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
                  title={t('common.share')}
                >
                  <Share2 size={12} />
                </button>
              </div>
            )}
          </div>
        );
      })}
      {/* Conversation Delete Confirmation Dialog */}
      <DeleteConversationDialog
        isOpen={conversationConfirmDialog.isOpen}
        conversationTitle={conversationConfirmDialog.conversationTitle}
        onConfirm={async () => {
          if (conversationConfirmDialog.conversationId) {
            return onDeleteConversation(
              conversationConfirmDialog.conversationId
            );
          }
        }}
        onCancel={() => setConversationConfirmDialog({ isOpen: false })}
      />

      {/* Conversation Rename Dialog */}
      <RenameDialog
        isOpen={renameDialog.isOpen}
        title={t('conversation.renameTitle')}
        currentName={renameDialog.conversationTitle}
        onConfirm={newTitle => {
          if (renameDialog.conversationId) {
            const trimmedTitle = newTitle.trim();
            if (!trimmedTitle) {
              showToast(t('form.conversationNameRequired'), 'warning');
              return;
            }
            if (containsXSS(trimmedTitle)) {
              showToast(t('errors.invalidInput'), 'warning');
              return;
            }
            onRenameConversation(renameDialog.conversationId, trimmedTitle);
          }
          setRenameDialog({
            isOpen: false,
            conversationId: null,
            conversationTitle: '',
          });
        }}
        onCancel={() =>
          setRenameDialog({
            isOpen: false,
            conversationId: null,
            conversationTitle: '',
          })
        }
      />
    </>
  );
}, areConversationsEqual);

export default ConversationList;
